import { useEffect, useState } from 'react'
import { FileText, RotateCw } from 'lucide-react'
import { attachmentsApi } from '@/api/attachmentsApi'
import { AttachmentPreviewDialog } from '@/components/renderer/AttachmentPreviewDialog'
import { isPreviewableImage } from '@/lib/attachmentPreview'

interface Props {
  submissionId: string
  fieldId: string
  /** The row's id. Required to address one file on a multi-file field; sent for
   *  every thumb so a strip never has to know which kind of field it shows. */
  attachmentId?: string
  fileName: string
  mimeType: string
  /** The attachment's storageKey — genuinely changes when a file is replaced,
   *  unlike the DB row's own `id` (an upsert-by-replace keeps the same row). */
  storageKey: string
  /** Shown under the thumbnail — the owning form's name (cross-step context) or
   *  the field's own label (same-form context). Caller picks what's useful. */
  caption: string
  /** For the preview panel's header. */
  sizeBytes: number
}

/**
 * One evidence thumbnail: fetches its own download URL on mount (cheap — a URL
 * string, not the file bytes) so an image renders inline without a click, and a
 * non-image still opens correctly on click via the file icon.
 *
 * Shared by ProcedureAttachmentsPanel (evidence from OTHER steps of the procedure) and
 * FormRenderer's own completed-form strip (this form's OWN attachments, shown
 * once the field grid — and everything in it, including each field's inline
 * icon — goes `pointer-events-none` on completion).
 */
export function AttachmentThumb({
  submissionId,
  fieldId,
  attachmentId,
  fileName,
  mimeType,
  storageKey,
  caption,
  sizeBytes,
}: Props) {
  const [url, setUrl] = useState<string | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [failed, setFailed] = useState(false)
  // Bumped by the retry button — included in the effect's deps so clicking it
  // re-runs the fetch without needing a separate imperative fetch function.
  const [retryTick, setRetryTick] = useState(0)

  useEffect(() => {
    let cancelled = false
    // Cleared up front: on a replace (storageKey changed) the old URL must not
    // keep rendering while the new one loads — it points at a different object.
    setUrl(null)
    setFailed(false)
    attachmentsApi
      .getDownloadUrl(submissionId, fieldId, attachmentId)
      .then(res => {
        if (!cancelled) setUrl(res.url)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [submissionId, fieldId, attachmentId, storageKey, retryTick])

  if (failed) {
    return (
      <button
        type='button'
        title={`Couldn't load ${fileName} — click to retry`}
        onClick={() => setRetryTick(t => t + 1)}
        className='flex w-20 shrink-0 flex-col items-center gap-1 rounded-lg border border-dashed border-destructive/40 bg-white p-1.5 text-center text-destructive transition-colors hover:bg-destructive/5'
      >
        <div className='flex h-14 w-full items-center justify-center rounded bg-muted/40'>
          <RotateCw className='h-4 w-4' />
        </div>
        <span className='w-full truncate text-[10px]'>Retry</span>
      </button>
    )
  }

  // A button, not a link to the presigned URL: reading the file now happens in
  // the panel, so the record stays loaded and no copy lands on the reader's
  // machine. Download is still one click away, inside the panel.
  return (
    <>
      <button
        type='button'
        onClick={() => setPreviewOpen(true)}
        title={`${fileName} — ${caption}`}
        className='group flex w-20 shrink-0 flex-col items-center gap-1 rounded-lg border border-slate-200 bg-white p-1.5 text-center transition-colors hover:border-primary/40'
      >
        <div className='flex h-14 w-full items-center justify-center overflow-hidden rounded bg-muted/40'>
          {url && isPreviewableImage(mimeType) ? (
            <img src={url} alt={fileName} className='h-full w-full object-cover' />
          ) : (
            <FileText className='h-5 w-5 text-muted-foreground' />
          )}
        </div>
        <span className='w-full truncate text-[10px] text-muted-foreground' title={caption}>
          {caption}
        </span>
      </button>

      {previewOpen && (
        <AttachmentPreviewDialog
          submissionId={submissionId}
          fieldId={fieldId}
          attachmentId={attachmentId}
          fileName={fileName}
          sizeBytes={sizeBytes}
          open={previewOpen}
          onOpenChange={setPreviewOpen}
        />
      )}
    </>
  )
}
